// Route table.
//
// HashRouter is kept from the previous build so existing `#/sessions` bookmarks
// still resolve, and because a static host has no rewrite rules to send deep
// links back to index.html.
import { Suspense, lazy, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './auth';
import Layout from './components/Layout';
import Login from './components/Login';
import { Splash } from './components/ui';

// Each screen is its own chunk, so the first paint only carries the shell.
const Dashboard = lazy(() => import('./components/Dashboard'));
const SessionsList = lazy(() => import('./components/SessionsList'));
const SessionDetail = lazy(() => import('./components/SessionDetail'));
const UploadPage = lazy(() => import('./components/UploadPage'));
const Settings = lazy(() => import('./components/Settings'));
const AlgorithmScripts = lazy(() => import('./components/AlgorithmScripts'));

/**
 * Gate for admin-only screens.
 *
 * A second line of defence, not the only one: every admin route on the server
 * checks the role itself. This exists so a clinician who types the URL gets sent
 * somewhere sensible instead of watching a screen fail to load.
 */
function AdminOnly({ children }: { children: React.ReactElement }) {
  const { isAdmin } = useAuth();
  return isAdmin ? children : <Navigate to="/dashboard" replace />;
}

/**
 * Fixture gallery for the charts, at #/lab.
 *
 * Development builds only, and deliberately outside the auth gate: its whole
 * point is to be reviewable without credentials and without a server. In a
 * production build `import.meta.env.DEV` is replaced with `false`, so this is
 * `null`, the dynamic import sits in dead code, and neither the gallery nor the
 * fixtures reach the bundle.
 */
const ChartLab = import.meta.env.DEV ? lazy(() => import('./components/ChartLab')) : null;

/**
 * A screen's chunk is usually cached or quick; only show the splash if it
 * is still loading after this long, so ordinary navigation doesn't flash it.
 */
const ROUTE_SPLASH_DELAY_MS = 500;

function RouteLoading() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setShow(true), ROUTE_SPLASH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, []);
  return show ? <Splash /> : null;
}

/** How long "Signing out…" stays up after the session ends. */
const SIGN_OUT_SPLASH_MS = 900;

export default function App() {
  const { status } = useAuth();
  const { pathname } = useLocation();
  const signingOut = useSigningOut(status);

  const overlay = signingOut && (
    <div className="fixed inset-0 z-99999">
      <Splash message="Signing out..." />
    </div>
  );

  if (ChartLab && pathname.startsWith('/lab')) {
    return (
      <>
        {overlay}
        <Suspense fallback={<Splash />}>
          <ChartLab />
        </Suspense>
      </>
    );
  }

  // Firebase restores its session asynchronously. Rendering routes before that
  // resolves would flash the login screen at someone who is already signed in.
  if (status === 'loading') return overlay || <Splash />;

  if (status !== 'ready') {
    return (
      <>
        {overlay}
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </>
    );
  }

  return (
    <>
      {overlay}
      <Suspense fallback={<RouteLoading />}>
        <Routes>
          {/* Already signed in: don't leave the login form reachable. */}
          <Route path="/login" element={<Navigate to="/dashboard" replace />} />
          <Route element={<Layout />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/sessions" element={<SessionsList />} />
            <Route path="/sessions/:id" element={<SessionDetail />} />
            <Route path="/upload" element={<UploadPage />} />
            <Route
              path="/algorithms"
              element={
                <AdminOnly>
                  <AlgorithmScripts />
                </AdminOnly>
              }
            />
            {/* Everyone's own account and theme; nothing admin-only lives here. */}
            <Route path="/settings" element={<Settings />} />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Suspense>
    </>
  );
}

/**
 * True for a moment after a signed-in session ends, so signing out shows a
 * splash instead of cutting straight to the login form.
 */
function useSigningOut(status: string): boolean {
  const [signingOut, setSigningOut] = useState(false);
  const previous = useRef(status);
  const timer = useRef<number | null>(null);

  useLayoutEffect(() => {
    const wasReady = previous.current === 'ready';
    previous.current = status;
    if (!wasReady || status === 'ready') return;

    setSigningOut(true);
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      setSigningOut(false);
      timer.current = null;
    }, SIGN_OUT_SPLASH_MS);
  }, [status]);

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  return signingOut;
}
